import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "flag-icon-css/css/flag-icons.min.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [phoneDigits, setPhoneDigits] = useState(""); // digits (no +) from react-phone-input-2
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isValidEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  const phoneE164 = useMemo(() => {
    if (!phoneDigits) return "";
    const raw = phoneDigits.startsWith("+") ? phoneDigits : `+${phoneDigits}`;
    const parsed = parsePhoneNumberFromString(raw);
    return parsed?.isValid() ? parsed.number : "";
  }, [phoneDigits]);

  const canSubmit = useMemo(() => {
    if (!password.trim()) return false;
    if (mode === "email") return isValidEmail(email);
    return Boolean(phoneE164);
  }, [mode, email, phoneE164, password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError("");

    const cleanPassword = password.trim();
    if (!cleanPassword) {
      setError("Password is required.");
      return;
    }

    let identifier = "";

    if (mode === "email") {
      const cleanEmail = String(email || "")
        .trim()
        .toLowerCase();
      if (!isValidEmail(cleanEmail)) {
        setError("Please enter a valid email.");
        return;
      }
      identifier = cleanEmail;
    } else {
      if (!phoneE164) {
        setError("Please enter a valid phone number.");
        return;
      }
      identifier = phoneE164; // E.164
    }

    setSubmitting(true);
    try {
      const res = await axios.post("/auth/login", {
        identifier,
        password: cleanPassword,
      });

      const { token, user } = res.data || {};
      if (!token) {
        setError("Login failed: token missing in server response.");
        return;
      }

      // IMPORTANT: wait for AuthContext to store token + hydrate from /profile
      const decoded = await login(token);

      const role = user?.role || decoded?.role;

      navigate(role === "admin" || role === "superadmin" ? "/admin" : "/user", {
        replace: true,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Login failed. Please try again.";
      setError(msg);
      console.error("Login error:", err?.response || err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
            Welcome back
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Sign in to manage your transactions
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-6"
        >
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
              {error}
            </div>
          )}

          <div className="space-y-3">
            {/* Mode Switch */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setMode("email");
                  setError("");
                }}
                className={`flex-1 rounded-xl px-3 py-2 text-sm border ${
                  mode === "email"
                    ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                    : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 border-black/10 dark:border-white/10"
                }`}
                disabled={submitting}
              >
                Email
              </button>

              <button
                type="button"
                onClick={() => {
                  setMode("phone");
                  setError("");
                }}
                className={`flex-1 rounded-xl px-3 py-2 text-sm border ${
                  mode === "phone"
                    ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                    : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 border-black/10 dark:border-white/10"
                }`}
                disabled={submitting}
              >
                Phone
              </button>
            </div>

            {mode === "email" ? (
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={submitting}
              />
            ) : (
              <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-2 py-2">
                <PhoneInput
                  country={"cm"}
                  value={phoneDigits}
                  onChange={(value) => setPhoneDigits(value)}
                  enableSearch
                  inputStyle={{
                    width: "100%",
                    border: "none",
                    height: "42px",
                    background: "transparent",
                  }}
                  buttonStyle={{
                    border: "none",
                    background: "transparent",
                  }}
                  containerStyle={{ width: "100%" }}
                  inputProps={{ name: "phone", disabled: submitting }}
                  disabled={submitting}
                />
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {phoneE164
                    ? `${phoneE164}`
                    : "Enter a valid number (with country code)."}
                </div>
              </div>
            )}

            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              autoComplete="current-password"
              disabled={submitting}
            />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm text-slate-600 dark:text-slate-300 hover:underline"
                disabled={submitting}
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="w-full rounded-xl bg-slate-900 text-white py-3 font-medium hover:opacity-95 transition
                         disabled:opacity-50 disabled:cursor-not-allowed
                         dark:bg-white dark:text-slate-900"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </button>

            <p className="text-center text-sm text-slate-600 dark:text-slate-300">
              Don&apos;t have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                disabled={submitting}
              >
                Create one
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ disabled, ...props }) {
  return (
    <input
      {...props}
      disabled={disabled}
      className="w-full rounded-xl border border-black/10 dark:border-white/10
                 bg-white dark:bg-slate-950 px-4 py-3 text-sm
                 text-slate-900 dark:text-white placeholder:text-slate-400
                 focus:outline-none focus:ring-2 focus:ring-slate-900/20 dark:focus:ring-white/20
                 disabled:opacity-60 disabled:cursor-not-allowed"
    />
  );
}
